import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Receipt, 
  LogOut, 
  Download, 
  Clock, 
  CheckCircle, 
  XCircle,
  Loader2,
  User,
  Mail,
  Phone,
  MapPin
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import NoindexHead from "@/components/NoindexHead";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e63e5c23-c73b-48e4-b5bd-c8da1ff8d651/artifacts/25pknzhh_ee436b9cf26fbe3a949749993a8a0799c33dc4d497d0bf4b65f923b400bf1fc0.jpeg";
const API = `${window.location.origin}/api`;

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-800",
  converted: "bg-blue-100 text-blue-800"
};

const statusIcons = {
  pending: Clock,
  accepted: CheckCircle,
  declined: XCircle,
  converted: CheckCircle
};

export default function CustomerPortal() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [customer, setCustomer] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/me`);
      setCustomer(response.data);
      setIsLoggedIn(true);
      localStorage.setItem("portal_session", "1");
      fetchDashboard();
    } catch (error) {
      localStorage.removeItem("portal_session");
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The httpOnly cookie carries the session; verify it server-side on mount.
    checkAuth();
  }, [checkAuth]);

  const fetchDashboard = async () => {
    try {
      const [dashRes, quotesRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/portal/dashboard`),
        axios.get(`${API}/portal/quotes`),
        axios.get(`${API}/portal/invoices`)
      ]);
      setDashboard(dashRes.data);
      setQuotes(quotesRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/portal/login`, { email, password });
      // Server set a secure httpOnly cookie; persist only a non-sensitive flag.
      localStorage.setItem("portal_session", "1");
      setCustomer({ name: response.data.customer_name, email: response.data.customer_email });
      setIsLoggedIn(true);
      fetchDashboard();
      toast.success("Welcome back!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await axios.post(`${API}/portal/logout`); } catch (e) { /* best-effort */ }
    localStorage.removeItem("portal_session");
    setIsLoggedIn(false);
    setCustomer(null);
    setDashboard(null);
    setQuotes([]);
    setInvoices([]);
    toast.success("Logged out successfully");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Login Form
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Helmet>
          <title>Customer Portal | Sustainable Handyman Services</title>
          <meta name="description" content="Log in to view your quotes and invoices from Sustainable Handyman Services." />
        </Helmet>
        
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={LOGO_URL} alt="Logo" className="h-16 w-16 rounded-full mx-auto mb-4 object-cover" />
            <CardTitle className="text-2xl font-heading">Customer Portal</CardTitle>
            <CardDescription>View your quotes and invoices</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="portal-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  data-testid="portal-password-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loginLoading} data-testid="portal-login-btn">
                {loginLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Log In
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              Don't have access? Contact us to set up your account.
            </p>
            <div className="text-center mt-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                ← Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Customer Dashboard
  return (
    <div className="min-h-screen bg-background">
      <NoindexHead title="My Account | Sustainable Handyman Services" />
      <Helmet>
        <title>My Account | Sustainable Handyman Services</title>
      </Helmet>
      
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <h1 className="font-heading font-bold">Customer Portal</h1>
              <p className="text-sm text-white/80">Welcome, {customer?.name || customer?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Log Out
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" data-testid="portal-dashboard-tab">Dashboard</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="portal-quotes-tab">Quotes ({quotes.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="portal-invoices-tab">Invoices ({invoices.length})</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid gap-6 md:grid-cols-3 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-blue-100">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Quotes</p>
                      <p className="text-2xl font-bold">{dashboard?.total_quotes || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-green-100">
                      <Receipt className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Invoices</p>
                      <p className="text-2xl font-bold">{dashboard?.total_invoices || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-yellow-100">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pending Quotes</p>
                      <p className="text-2xl font-bold">{dashboard?.pending_quotes || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Customer Info */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Your Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <span>{customer?.name || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <span>{customer?.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground" />
                    <span>{customer?.phone || '-'}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <span>{customer?.address || '-'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            {quotes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No quotes yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => {
                  const StatusIcon = statusIcons[quote.status] || Clock;
                  return (
                    <Card key={quote.id} data-testid={`quote-card-${quote.id}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold">{quote.reference}</h3>
                              <Badge className={statusColors[quote.status]}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {quote.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{quote.description || 'No description'}</p>
                            <p className="text-sm text-muted-foreground">
                              Date: {formatDate(quote.date)} | Valid until: {formatDate(quote.valid_until)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">{formatCurrency(quote.total)}</p>
                            {quote.pdf_url && (
                              <Button variant="outline" size="sm" className="mt-2" asChild>
                                <a href={quote.pdf_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="h-4 w-4 mr-2" />
                                  Download PDF
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                        {quote.line_items?.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium mb-2">Items:</p>
                            <div className="space-y-1">
                              {quote.line_items.map((item, idx) => (
                                <div key={`${item.description}-${idx}`} className="flex justify-between text-sm">
                                  <span>{item.description} × {item.quantity}</span>
                                  <span>{formatCurrency(item.quantity * item.unit_cost)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            {invoices.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No invoices yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {invoices.map((invoice) => (
                  <Card key={invoice.id} data-testid={`invoice-card-${invoice.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-semibold mb-2">{invoice.reference}</h3>
                          <p className="text-sm text-muted-foreground mb-1">{invoice.description || 'No description'}</p>
                          <p className="text-sm text-muted-foreground">
                            Date: {formatDate(invoice.date)} | Payment due in {invoice.payment_days} days
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">{formatCurrency(invoice.total)}</p>
                          {invoice.pdf_url && (
                            <Button variant="outline" size="sm" className="mt-2" asChild>
                              <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </a>
                            </Button>
                          )}
                        </div>
                      </div>
                      {invoice.line_items?.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium mb-2">Items:</p>
                          <div className="space-y-1">
                            {invoice.line_items.map((item, idx) => (
                              <div key={`${item.description}-${idx}`} className="flex justify-between text-sm">
                                <span>{item.description} × {item.quantity}</span>
                                <span>{formatCurrency(item.quantity * item.unit_cost)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-6 text-center text-sm text-muted-foreground">
        <p>Sustainable Handyman Services | 07359 139941 | Sandy@sustainablehandymanservices.uk</p>
      </footer>
    </div>
  );
}
